import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBuilding,
  faCheck,
  faEnvelope,
  faFileLines,
  faRotateRight,
  faShieldHalved,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { useBranding } from '../App/hooks/useBranding';
import logo from 'assets/icons/logo.png';
import { inspectSupplierInvitation } from './invitationApi';
import { SupplierInvitationKind } from './invitationContracts';
import { usePasswordlessEmailCode } from './usePasswordlessEmailCode';
import { useSupplierInvitationClaim } from './useSupplierInvitation';
import { appendRedirectUrl } from '../Auth/redirectUrl';
import { cn } from 'modules/Common/utils/cn';

// Ported from SupplierInvitationPage.scss (tailwind-standardization phase 6).
// Colors are the page's own one-off palette (not the app's semantic tokens)
// so they're hardcoded arbitrary values rather than theme colors; `#a86612`
// keeps its distinct amber and `#b42318` its distinct red — neither is meant
// to track the shared warning/danger tokens.
const SUPPLIER_INVITE_BASE = cn(
  'min-h-screen font-sans text-[#17252b]',
  // background: 90deg hairline grid over the page's paper color. Image/
  // position/color are static (set via inline style below); only the grid
  // pitch changes across the breakpoint, so it stays a Tailwind class with
  // no unprefixed counterpart (see CLAUDE.md's base-vs-variant cascade note).
  'max-[900px]:bg-[length:48px_48px] min-[901px]:bg-[length:72px_72px]',
  'max-[900px]:px-5 max-[900px]:py-5 max-[900px]:pb-10',
  'min-[901px]:px-[clamp(20px,5vw,76px)] min-[901px]:pt-7 min-[901px]:pb-12',
);
const SUPPLIER_INVITE_BG_STYLE = {
  backgroundColor: '#f6f8f7',
  backgroundImage: 'linear-gradient(90deg, rgba(220, 231, 226, 0.36) 1px, transparent 1px)',
  backgroundPosition: '0 0',
};
const SUPPLIER_LOGO_CLASSES = 'block h-[34px] w-auto max-w-[190px] object-contain';
const SUPPLIER_GRID_CLASSES = cn(
  'mx-auto grid w-[min(1180px,100%)] items-start',
  'max-[900px]:grid-cols-[minmax(0,1fr)] max-[900px]:gap-[34px]',
  'min-[901px]:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.72fr)] min-[901px]:gap-[clamp(48px,8vw,112px)]',
);
const SUPPLIER_EYEBROW_CLASSES =
  'mb-3.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-primaryColor';
const SUPPLIER_STATE_HEADING_CLASSES =
  'm-0 text-[clamp(24px,2.5vw,31px)] font-[680] leading-[1.12] tracking-[-0.03em] text-[#17252b]';
const SUPPLIER_STATE_DESCRIPTION_CLASSES = 'mb-6 mt-3.5 text-sm leading-[1.6] text-[#5a6863]';
const SUPPLIER_ICON_BADGE_CLASSES =
  'mb-[22px] inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primaryColor/10 text-primaryColor';
const SUPPLIER_ICON_BADGE_WARNING_CLASSES = cn(
  SUPPLIER_ICON_BADGE_CLASSES,
  'bg-[#fff1f0] text-[#b42318]',
);
const SUPPLIER_BUTTON_BASE_CLASSES = cn(
  'inline-flex min-h-[48px] w-full items-center justify-center gap-2.5',
  'rounded-[10px] border border-transparent px-4 py-[11px] text-sm font-[750] no-underline',
  'transition-[background-color,border-color,transform] duration-[160ms] ease-in-out',
  'motion-reduce:transition-none',
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-primaryColor/[0.24]',
  'disabled:cursor-wait disabled:opacity-[0.64]',
);
const SUPPLIER_BUTTON_PRIMARY_CLASSES = cn(
  SUPPLIER_BUTTON_BASE_CLASSES,
  'bg-primaryColor text-white',
  'enabled:hover:-translate-y-px enabled:hover:bg-primaryColor-dark enabled:hover:text-white',
);
const SUPPLIER_BUTTON_SECONDARY_CLASSES = cn(
  SUPPLIER_BUTTON_BASE_CLASSES,
  'border-[#cbd8d2] bg-white text-[#17252b]',
);
const SUPPLIER_TEXT_BUTTON_CLASSES = cn(
  'm-0 border-0 bg-transparent p-0 text-xs font-bold text-[#54635e]',
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-primaryColor/[0.24]',
);
const SUPPLIER_INPUT_BASE_CLASSES = cn(
  'mb-3.5 h-12 w-full rounded-[10px] border border-[#cbd8d2] bg-white px-3.5',
  'text-[15px] text-[#17252b] outline-none',
  'transition-[border-color,box-shadow] duration-[160ms] ease-in-out motion-reduce:transition-none',
  'focus:border-primaryColor focus:shadow-[0_0_0_3px_rgb(var(--primary-color)/0.14)]',
);
const SUPPLIER_CODE_INPUT_CLASSES = cn(
  SUPPLIER_INPUT_BASE_CLASSES,
  'text-center font-mono text-[21px] tracking-[0.3em]',
);
const SUPPLIER_SKELETON_CLASSES = cn(
  'rounded-[9px] bg-[length:200%_100%] bg-[position:0_0]',
  'bg-[linear-gradient(90deg,#e3ebe7_25%,#f2f5f3_50%,#e3ebe7_75%)]',
  'animate-skeletonShimmer motion-reduce:animate-none',
);

// Built per-render with the current brand name (whitelabel builds must not
// say "Carbon Glance support") — a plain object can't reach useBranding()
// since it lives outside the component. `t` is threaded through the same way
// for the same reason.
const buildSupportMessage = (brandName, t) => ({
  identity_mismatch: {
    title: t(
      'freeSupplier:invitation.support.identityMismatch.title',
      'Use an email from the invited company',
    ),
    body: t(
      'freeSupplier:invitation.support.identityMismatch.body',
      'This invitation belongs to another corporate domain. Sign out, then verify an email address from the invited supplier company.',
    ),
  },
  manual_review_required: {
    title: t(
      'freeSupplier:invitation.support.manualReviewRequired.title',
      'This workspace needs a quick review',
    ),
    body: t(
      'freeSupplier:invitation.support.manualReviewRequired.body',
      'We found more than one possible workspace for this company. {{brandName}} support must confirm the correct one before you continue.',
      { brandName },
    ),
  },
  workspace_suspended: {
    title: t(
      'freeSupplier:invitation.support.workspaceSuspended.title',
      'This supplier workspace is unavailable',
    ),
    body: t(
      'freeSupplier:invitation.support.workspaceSuspended.body',
      'The workspace has been suspended. Contact your workspace administrator or {{brandName}} support.',
      { brandName },
    ),
  },
  retryable_error: {
    title: t(
      'freeSupplier:invitation.support.retryableError.title',
      'The workspace is not ready yet',
    ),
    body: t(
      'freeSupplier:invitation.support.retryableError.body',
      'Your invitation is still safe. Try again to resume from the last completed step.',
    ),
  },
});

const InvitationSkeleton = () => {
  const { t } = useTranslation('freeSupplier');
  return (
    <div
      className={SUPPLIER_INVITE_BASE}
      style={SUPPLIER_INVITE_BG_STYLE}
      aria-label={t('freeSupplier:invitation.loadingAriaLabel', 'Checking supplier invitation')}
      role="status"
    >
      <div
        className={cn(
          SUPPLIER_SKELETON_CLASSES,
          'h-8 w-[170px] max-[900px]:mb-11 min-[901px]:mb-[74px]',
        )}
      />
      <div className={SUPPLIER_GRID_CLASSES}>
        <div>
          <div className={cn(SUPPLIER_SKELETON_CLASSES, 'mb-[22px] h-[14px] w-[220px]')} />
          <div
            className={cn(
              SUPPLIER_SKELETON_CLASSES,
              'mb-6 w-[90%] max-[900px]:h-24 min-[901px]:h-[120px]',
            )}
          />
          <div className={cn(SUPPLIER_SKELETON_CLASSES, 'h-[52px] w-3/4')} />
          <div className={cn(SUPPLIER_SKELETON_CLASSES, 'mt-[58px] h-[74px] w-[95%]')} />
        </div>
        <div
          className={cn(
            SUPPLIER_SKELETON_CLASSES,
            'w-full max-[900px]:h-[380px] min-[901px]:h-[430px]',
          )}
        />
      </div>
    </div>
  );
};

const EvidenceRail = ({ signedIn, claimStatus }) => {
  const { t } = useTranslation('freeSupplier');
  const current = claimStatus === 'activating' || claimStatus === 'complete' ? 2 : signedIn ? 1 : 0;
  const steps = [
    [
      t('freeSupplier:invitation.evidence.received.title', 'Invitation received'),
      t('freeSupplier:invitation.evidence.received.detail', 'Customer and request confirmed'),
    ],
    [
      t('freeSupplier:invitation.evidence.emailVerified.title', 'Corporate email verified'),
      t('freeSupplier:invitation.evidence.emailVerified.detail', 'A named same-domain identity'),
    ],
    [
      t('freeSupplier:invitation.evidence.workspaceReady.title', 'Supplier workspace ready'),
      t(
        'freeSupplier:invitation.evidence.workspaceReady.detail',
        'One company domain, one workspace',
      ),
    ],
  ];
  return (
    <ol
      className={cn(
        'mb-0 ml-0 mr-0 grid max-w-[720px] list-none pl-0',
        'max-[900px]:mt-9 max-[900px]:grid-cols-[1fr] max-[900px]:gap-[18px]',
        'min-[901px]:mt-[clamp(42px,7vh,68px)] min-[901px]:grid-cols-3',
      )}
      aria-label={t('freeSupplier:invitation.evidence.ariaLabel', 'Supplier invitation progress')}
    >
      {steps.map(([title, detail], index) => {
        const isActive = index <= current;
        return (
          <li
            key={title}
            className={cn(
              'relative flex min-w-0 gap-[11px] pr-[18px] text-[#77837f]',
              "after:absolute after:bg-[#dce7e2] after:content-['']",
              'max-[900px]:after:bottom-[-19px] max-[900px]:after:left-[13px] max-[900px]:after:right-auto max-[900px]:after:top-[27px] max-[900px]:after:h-[20px] max-[900px]:after:w-px max-[900px]:after:translate-x-0',
              'min-[901px]:after:left-[35px] min-[901px]:after:right-[6px] min-[901px]:after:top-[14px] min-[901px]:after:h-px min-[901px]:after:translate-x-[56%]',
              'last:after:hidden',
              isActive && 'text-[#17252b]',
            )}
          >
            <span
              className={cn(
                'relative z-10 inline-flex h-7 w-7 flex-none items-center justify-center',
                'rounded-full border border-[#cfdad5] bg-[#f6f8f7] font-mono text-[11px]',
                isActive && 'border-primaryColor bg-primaryColor text-white',
              )}
            >
              {index < current ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
            </span>
            <span>
              <strong className="mt-[3px] block text-xs leading-[1.3]">{title}</strong>
              <small
                className={cn(
                  'mt-[5px] block leading-[1.4] text-[#78847f]',
                  'max-[900px]:text-[11px] min-[901px]:text-[10px]',
                )}
              >
                {detail}
              </small>
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const RequestContext = ({ invitation }) => {
  const { t } = useTranslation('freeSupplier');
  if (invitation.kind !== SupplierInvitationKind.DeclarantDataRequest || !invitation.request) {
    return null;
  }
  return (
    <section
      className="mt-[30px] max-w-[650px] rounded-xl border border-[#e7d5bb] border-l-4 border-l-[#a86612] bg-[rgba(255,251,245,0.88)] px-5 py-[18px]"
      aria-labelledby="supplier-request-title"
    >
      <div className="flex items-center gap-3 text-[#a86612]">
        <FontAwesomeIcon icon={faFileLines} />
        <div className="flex flex-col">
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#7a6242]">
            {t('freeSupplier:invitation.requestedEmissionsData', 'Requested emissions data')}
          </span>
          <strong
            id="supplier-request-title"
            className="font-mono text-sm text-[#4d3a22]"
          >
            {invitation.request.reportingPeriod}
          </strong>
        </div>
      </div>
      <div className="mt-3.5 flex flex-wrap gap-2">
        {invitation.request.goods.map((good) => (
          <code
            key={good}
            className="rounded-md border border-[#e9d9c2] bg-white px-2 py-[5px] text-xs text-[#4d3a22]"
          >
            CN {good}
          </code>
        ))}
      </div>
    </section>
  );
};

const UnavailablePanel = ({ brand }) => {
  const { t } = useTranslation('freeSupplier');
  return (
    <main
      className={cn(SUPPLIER_INVITE_BASE, 'flex flex-col items-center justify-center gap-7')}
      style={SUPPLIER_INVITE_BG_STYLE}
    >
      <img
        className={SUPPLIER_LOGO_CLASSES}
        src={brand.logoUrl || logo}
        alt={t('freeSupplier:invitation.brandLogoAlt', '{{brandName}} logo', {
          brandName: brand.name,
        })}
      />
      <section className="w-[min(480px,100%)] rounded-2xl border border-[#d9e3df] bg-white p-[38px] text-center shadow-[0_24px_70px_rgba(24,51,42,0.1)]">
        <span className={SUPPLIER_ICON_BADGE_WARNING_CLASSES}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </span>
        <p className={SUPPLIER_EYEBROW_CLASSES}>
          {t('freeSupplier:invitation.unavailable.eyebrow', 'Invitation unavailable')}
        </p>
        <h1 className={SUPPLIER_STATE_HEADING_CLASSES}>
          {t('freeSupplier:invitation.unavailable.title', 'This link can no longer be used')}
        </h1>
        <p className="mt-4 leading-[1.6] text-[#5a6863]">
          {t(
            'freeSupplier:invitation.unavailable.body',
            'It may have expired, been revoked, or already been used. Ask the sender for a new one-use supplier invitation.',
          )}
        </p>
      </section>
    </main>
  );
};

export default function SupplierInvitationPage() {
  const { t } = useTranslation('freeSupplier');
  const { token } = useParams();
  const history = useHistory();
  const clerk = useClerk();
  const brand = useBranding();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const inspection = useQuery({
    queryKey: ['freeSupplierInvitation', 'inspect', token],
    queryFn: () => inspectSupplierInvitation(token),
    retry: false,
    staleTime: 60_000,
  });
  const invitation = inspection.data?.status === 'available' ? inspection.data : null;
  const { claim, status: claimStatus, error: claimError } = useSupplierInvitationClaim(token);
  const passwordless = usePasswordlessEmailCode({ onAuthenticated: claim });

  const copy = useMemo(() => {
    if (!invitation) return null;
    if (invitation.kind === SupplierInvitationKind.Platform) {
      return {
        eyebrow: t(
          'freeSupplier:invitation.copy.platform.eyebrow',
          '{{brandName}} supplier invitation',
          {
            brandName: brand.name,
          },
        ),
        title: t(
          'freeSupplier:invitation.copy.platform.title',
          'Build one supplier workspace. Share verified data everywhere.',
        ),
        body: t(
          'freeSupplier:invitation.copy.platform.body',
          'Use your corporate email to create or join your company workspace. This one-use invitation is not tied to a particular customer.',
        ),
      };
    }
    return {
      eyebrow: t(
        'freeSupplier:invitation.copy.customer.eyebrow',
        '{{declarantName}} invited your company',
        {
          declarantName:
            invitation.declarantName || t('freeSupplier:invitation.copy.aCustomer', 'A customer'),
        },
      ),
      title:
        invitation.kind === SupplierInvitationKind.DeclarantDataRequest
          ? t(
              'freeSupplier:invitation.copy.customer.dataRequestTitle',
              'Answer this emissions request without another spreadsheet chain.',
            )
          : t(
              'freeSupplier:invitation.copy.customer.defaultTitle',
              'Create your supplier workspace and connect once.',
            ),
      body: t(
        'freeSupplier:invitation.copy.customer.body',
        '{{declarantName}} will receive the data your company chooses to publish. Verify a corporate email to continue.',
        {
          declarantName:
            invitation.declarantName ||
            t('freeSupplier:invitation.copy.yourCustomer', 'Your customer'),
        },
      ),
    };
  }, [invitation, brand.name, t]);
  const supportMessage = useMemo(() => buildSupportMessage(brand.name, t), [brand.name, t]);

  if (inspection.isLoading || !authLoaded) return <InvitationSkeleton />;
  if (
    inspection.isError ||
    inspection.data?.status === 'unavailable' ||
    claimStatus === 'unavailable'
  ) {
    return <UnavailablePanel brand={brand} />;
  }

  const claimBusy = claimStatus === 'claiming' || claimStatus === 'activating';
  const errorCopy = claimError
    ? supportMessage[claimError.status] || supportMessage.retryable_error
    : null;
  const signedInEmail = user?.primaryEmailAddress?.emailAddress;
  const signInUrl = appendRedirectUrl('/sign-in', `/join/${token}`);
  const useAnotherAccount = async () => {
    await clerk.signOut();
    history.replace(signInUrl);
  };

  return (
    <main
      className={SUPPLIER_INVITE_BASE}
      style={SUPPLIER_INVITE_BG_STYLE}
    >
      <header
        className={cn(
          'flex min-h-12 items-center justify-between',
          'text-xs font-bold uppercase tracking-[0.09em] text-[#5f6d68]',
          'max-[900px]:mb-9 min-[901px]:mb-[clamp(40px,7vh,78px)]',
        )}
      >
        <img
          className={SUPPLIER_LOGO_CLASSES}
          src={brand.logoUrl || logo}
          alt={t('freeSupplier:invitation.brandLogoAlt', '{{brandName}} logo', {
            brandName: brand.name,
          })}
        />
        <span className="max-[900px]:hidden">
          {t('freeSupplier:invitation.headerTagline', 'Free supplier workspace')}
        </span>
      </header>

      <div className={SUPPLIER_GRID_CLASSES}>
        <section>
          <p className={SUPPLIER_EYEBROW_CLASSES}>{copy.eyebrow}</p>
          <h1
            className={cn(
              'm-0 max-w-[720px] font-[690] leading-[1.01] tracking-[-0.045em] text-[#17252b]',
              'max-[900px]:text-[clamp(34px,10vw,48px)] min-[901px]:text-[clamp(36px,4.4vw,64px)]',
            )}
          >
            {copy.title}
          </h1>
          <p
            className={cn(
              'mb-0 mt-6 max-w-[650px] leading-[1.65] text-[#52615c]',
              'max-[900px]:text-[15px] min-[901px]:text-[17px]',
            )}
          >
            {copy.body}
          </p>
          <RequestContext invitation={invitation} />
          <EvidenceRail
            signedIn={isSignedIn}
            claimStatus={claimStatus}
          />
          <p className="mb-0 mt-[30px] flex max-w-[620px] items-center gap-[9px] text-xs leading-[1.5] text-[#66746f]">
            <FontAwesomeIcon icon={faShieldHalved} />
            {t(
              'freeSupplier:invitation.privacyNote',
              'The invitation and your verified email—not browser storage—determine the workspace.',
            )}
          </p>
        </section>

        <section
          className="overflow-hidden rounded-2xl border border-[#d9e3df] bg-white/[94%] shadow-[0_24px_70px_rgba(24,51,42,0.11)]"
          aria-labelledby="supplier-access-title"
        >
          <div
            className="h-[5px] w-full bg-primaryColor"
            aria-hidden="true"
          />
          {errorCopy ? (
            <div className="max-[900px]:px-6 max-[900px]:py-7 min-[901px]:p-[clamp(28px,4vw,42px)]">
              <span className={SUPPLIER_ICON_BADGE_WARNING_CLASSES}>
                <FontAwesomeIcon icon={faTriangleExclamation} />
              </span>
              <p className={SUPPLIER_EYEBROW_CLASSES}>
                {t('freeSupplier:invitation.actionNeeded', 'Action needed')}
              </p>
              <h2
                id="supplier-access-title"
                className={SUPPLIER_STATE_HEADING_CLASSES}
              >
                {errorCopy.title}
              </h2>
              <p className={SUPPLIER_STATE_DESCRIPTION_CLASSES}>{errorCopy.body}</p>
              {claimError.status === 'retryable_error' && (
                <button
                  type="button"
                  className={SUPPLIER_BUTTON_PRIMARY_CLASSES}
                  onClick={claim}
                  disabled={claimBusy}
                >
                  <FontAwesomeIcon icon={faRotateRight} />
                  {t('freeSupplier:invitation.tryAgain', 'Try again')}
                </button>
              )}
              {claimError.status === 'identity_mismatch' && (
                <button
                  type="button"
                  className={SUPPLIER_BUTTON_SECONDARY_CLASSES}
                  onClick={useAnotherAccount}
                >
                  {t('freeSupplier:invitation.useAnotherAccount', 'Use another account')}
                </button>
              )}
            </div>
          ) : isSignedIn ? (
            <div className="max-[900px]:px-6 max-[900px]:py-7 min-[901px]:p-[clamp(28px,4vw,42px)]">
              <span className={SUPPLIER_ICON_BADGE_CLASSES}>
                <FontAwesomeIcon icon={faBuilding} />
              </span>
              <p className={SUPPLIER_EYEBROW_CLASSES}>
                {t('freeSupplier:invitation.corporateIdentity', 'Corporate identity')}
              </p>
              <h2
                id="supplier-access-title"
                className={SUPPLIER_STATE_HEADING_CLASSES}
              >
                {t(
                  'freeSupplier:invitation.continueWithVerifiedAccount',
                  'Continue with your verified account',
                )}
              </h2>
              <p className={SUPPLIER_STATE_DESCRIPTION_CLASSES}>
                {signedInEmail ? (
                  <>
                    {t('freeSupplier:invitation.signedInAs', 'You are signed in as')}{' '}
                    <strong>{signedInEmail}</strong>.
                  </>
                ) : (
                  t(
                    'freeSupplier:invitation.signedInEmailCheckNotice',
                    'Your signed-in corporate email will be checked against this invitation.',
                  )
                )}
              </p>
              <button
                type="button"
                className={SUPPLIER_BUTTON_PRIMARY_CLASSES}
                onClick={claim}
                disabled={claimBusy}
              >
                {claimStatus === 'claiming'
                  ? t('freeSupplier:invitation.acceptingInvitation', 'Accepting invitation…')
                  : claimStatus === 'activating'
                    ? t('freeSupplier:invitation.openingWorkspace', 'Opening workspace…')
                    : t('freeSupplier:invitation.acceptInvitation', 'Accept invitation')}
                {!claimBusy && <FontAwesomeIcon icon={faArrowRight} />}
              </button>
            </div>
          ) : (
            <div className="max-[900px]:px-6 max-[900px]:py-7 min-[901px]:p-[clamp(28px,4vw,42px)]">
              <span className={SUPPLIER_ICON_BADGE_CLASSES}>
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <p className={SUPPLIER_EYEBROW_CLASSES}>
                {t('freeSupplier:invitation.passwordlessAccess', 'Passwordless access')}
              </p>
              <h2
                id="supplier-access-title"
                className={SUPPLIER_STATE_HEADING_CLASSES}
              >
                {passwordless.step === 'email'
                  ? t('freeSupplier:invitation.verifyCorporateEmail', 'Verify a corporate email')
                  : t('freeSupplier:invitation.enterOneTimeCode', 'Enter your one-time code')}
              </h2>
              {passwordless.step === 'email' ? (
                <>
                  <p className={SUPPLIER_STATE_DESCRIPTION_CLASSES}>
                    {t(
                      'freeSupplier:invitation.corporateMailboxHint',
                      'Use a named or shared mailbox on your supplier company domain.',
                    )}
                  </p>
                  <label
                    htmlFor="supplier-email"
                    className="mb-2 block text-xs font-bold text-[#3f4e49]"
                  >
                    {t('freeSupplier:invitation.corporateEmailLabel', 'Corporate email')}
                  </label>
                  <input
                    id="supplier-email"
                    type="email"
                    className={SUPPLIER_INPUT_BASE_CLASSES}
                    autoComplete="email"
                    value={passwordless.email}
                    onChange={(event) => passwordless.setEmail(event.target.value)}
                    placeholder="name@yourcompany.com"
                    disabled={passwordless.busy}
                  />
                  <button
                    type="button"
                    className={SUPPLIER_BUTTON_PRIMARY_CLASSES}
                    onClick={passwordless.sendCode}
                    disabled={!passwordless.ready || passwordless.busy}
                  >
                    {passwordless.busy
                      ? t('freeSupplier:invitation.sendingCode', 'Sending code…')
                      : t('freeSupplier:invitation.sendOneTimeCode', 'Send a one-time code')}
                    {!passwordless.busy && <FontAwesomeIcon icon={faArrowRight} />}
                  </button>
                </>
              ) : (
                <>
                  <p className={SUPPLIER_STATE_DESCRIPTION_CLASSES}>
                    {t('freeSupplier:invitation.codeSentTo', 'We sent a six-digit code to')}{' '}
                    <strong>{passwordless.email}</strong>.
                  </p>
                  <label
                    htmlFor="supplier-code"
                    className="mb-2 block text-xs font-bold text-[#3f4e49]"
                  >
                    {t('freeSupplier:invitation.oneTimeCodeLabel', 'One-time code')}
                  </label>
                  <input
                    id="supplier-code"
                    className={SUPPLIER_CODE_INPUT_CLASSES}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={passwordless.code}
                    onChange={(event) =>
                      passwordless.setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    disabled={passwordless.busy}
                  />
                  <button
                    type="button"
                    className={SUPPLIER_BUTTON_PRIMARY_CLASSES}
                    onClick={passwordless.verifyCode}
                    disabled={passwordless.busy}
                  >
                    {passwordless.busy
                      ? t('freeSupplier:invitation.verifying', 'Verifying…')
                      : t('freeSupplier:invitation.verifyAndContinue', 'Verify and continue')}
                    {!passwordless.busy && <FontAwesomeIcon icon={faArrowRight} />}
                  </button>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-x-[18px] gap-y-1.5">
                    <button
                      type="button"
                      className={SUPPLIER_TEXT_BUTTON_CLASSES}
                      onClick={passwordless.resendCode}
                      disabled={passwordless.busy || passwordless.resendCooldown > 0}
                    >
                      {passwordless.resendCooldown > 0
                        ? t('freeSupplier:invitation.resendCodeIn', 'Resend code in {{seconds}}s', {
                            seconds: passwordless.resendCooldown,
                          })
                        : t('freeSupplier:invitation.resendCode', 'Resend code')}
                    </button>
                    <button
                      type="button"
                      className={SUPPLIER_TEXT_BUTTON_CLASSES}
                      onClick={passwordless.changeEmail}
                      disabled={passwordless.busy}
                    >
                      {t('freeSupplier:invitation.useDifferentEmail', 'Use a different email')}
                    </button>
                  </div>
                  {passwordless.resent && !passwordless.error && (
                    <p
                      className="mt-3 text-xs font-[650] leading-[1.5] text-primaryColor"
                      role="status"
                    >
                      {t(
                        'freeSupplier:invitation.newCodeOnWay',
                        'A new code is on its way to {{email}}.',
                        {
                          email: passwordless.email,
                        },
                      )}
                    </p>
                  )}
                </>
              )}
              {passwordless.error && (
                <p
                  className="mt-3.5 text-xs leading-[1.5] text-[#b42318]"
                  role="alert"
                >
                  {passwordless.error}
                </p>
              )}
              <p className="mt-6 border-t border-[#e7eeeb] pt-5 text-center text-xs text-[#73807b]">
                {t(
                  'freeSupplier:invitation.preferExistingSignIn',
                  'Prefer your existing sign-in method?',
                )}{' '}
                <Link
                  to={signInUrl}
                  className="font-[750] text-[#17252b]"
                >
                  {t('freeSupplier:invitation.signInInstead', 'Sign in instead')}
                </Link>
              </p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
